import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import howIEatService from '../services/howIEatService'
import { MEAL_TYPES, MenuItemNutritionPreview } from '../types/howIEat'
import { useTranslation } from '../hooks/use-i18n'

interface Props {
  menuItemId: string
  onClose: () => void
  onSuccess: () => void
}

const LogFromMenuModal: React.FC<Props> = ({ menuItemId, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const [preview, setPreview] = useState<MenuItemNutritionPreview | null>(null)
  const [mealType, setMealType] = useState('lunch')
  const [calories, setCalories] = useState('')
  const [proteinG, setProteinG] = useState('')
  const [carbsG, setCarbsG] = useState('')
  const [fatsG, setFatsG] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    howIEatService
      .previewMenuItem(menuItemId)
      .then((p) => {
        setPreview(p)
        if (p.calories != null) setCalories(String(p.calories))
        if (p.proteinG != null) setProteinG(String(p.proteinG))
        if (p.carbsG != null) setCarbsG(String(p.carbsG))
        if (p.fatsG != null) setFatsG(String(p.fatsG))
      })
      .catch(() => toast.error(t('toast.dishLoadFailed')))
      .finally(() => setLoading(false))
  }, [menuItemId])

  const save = async () => {
    const cal = Number(calories)
    const p = Number(proteinG)
    const c = Number(carbsG)
    const f = Number(fatsG)
    if (!cal || cal < 0 || p < 0 || c < 0 || f < 0) {
      toast.error(t('toast.validNutrition'))
      return
    }
    try {
      setIsSaving(true)
      await howIEatService.createMealFromMenu({
        menuItemId,
        mealType,
        calories: cal,
        proteinG: p,
        carbsG: c,
        fatsG: f,
      })
      toast.success(t('toast.loggedToHowIEat'))
      onSuccess()
      onClose()
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('error.howIEat.logMealFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl w-full max-w-md p-5">
        <h3 className="text-lg font-bold text-white mb-1">{t('howIEat.logFromMenu.title')}</h3>
        <p className="text-white/60 text-sm mb-4">{preview?.name || (loading ? t('howIEat.logFromMenu.loading') : t('howIEat.logFromMenu.dishFallback'))}</p>

        {preview?.hasNutrition && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 mb-4 text-sm">
            <p className="text-emerald-400 font-medium mb-1">{t('howIEat.logFromMenu.nutritionOnFile')}</p>
            <p className="text-white/70">
              {calories} cal · P {proteinG}g · C {carbsG}g · F {fatsG}g
            </p>
          </div>
        )}

        <p className="text-white/70 text-sm mb-2">{t('howIEat.logFromMenu.mealType')}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {MEAL_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setMealType(t)}
              className={`px-3 py-1.5 rounded-full text-sm capitalize ${
                mealType === t ? 'bg-emerald-600 text-white' : 'bg-white/10 text-white/70'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {!preview?.hasNutrition && (
          <>
            <p className="text-white/50 text-xs mb-2">{t('howIEat.logFromMenu.noNutrition')}</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <input placeholder={t('howIEat.logFromMenu.caloriesRequired')} value={calories} onChange={(e) => setCalories(e.target.value)} className="px-3 py-2 bg-white/10 rounded-lg text-white text-sm" />
              <input placeholder={t('howIEat.logFromMenu.proteinRequired')} value={proteinG} onChange={(e) => setProteinG(e.target.value)} className="px-3 py-2 bg-white/10 rounded-lg text-white text-sm" />
              <input placeholder={t('howIEat.logFromMenu.carbsRequired')} value={carbsG} onChange={(e) => setCarbsG(e.target.value)} className="px-3 py-2 bg-white/10 rounded-lg text-white text-sm" />
              <input placeholder={t('howIEat.logFromMenu.fatsRequired')} value={fatsG} onChange={(e) => setFatsG(e.target.value)} className="px-3 py-2 bg-white/10 rounded-lg text-white text-sm" />
            </div>
          </>
        )}

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/20 text-white">
            {t('howIEat.logFromMenu.cancel')}
          </button>
          <button
            type="button"
            disabled={isSaving || loading}
            onClick={save}
            className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold disabled:opacity-60"
          >
            {isSaving ? t('howIEat.logFromMenu.saving') : t('howIEat.logFromMenu.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default LogFromMenuModal
